import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useCookieConsent } from '../contexts/CookieConsentContext';
import { trackCookieConsentUpdated } from '../utils/analytics';
import { useLocale } from '../contexts/LocaleContext';
import { Button } from './ui/button';
import { CancelButton } from './ui/cancel-button';
import { Card } from './ui/card';
import { Switch } from './ui/switch';
import { X, Settings, Check } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

export const CookieConsentBanner: React.FC = () => {
  const { showBanner, setConsent, setShowBanner, consent } = useCookieConsent();
  const { t } = useLocale();
  const [showDetails, setShowDetails] = useState(false);
  const [tempConsent, setTempConsent] = useState({
    necessary: true,
    analytics: consent?.analytics ?? false,
  });

  if (!showBanner) {
    return null;
  }

  const handleAcceptAll = (): void => {
    setConsent({ necessary: true, analytics: true });
    trackCookieConsentUpdated('accept_all', true);
  };

  const handleRejectAll = (): void => {
    setConsent({ necessary: true, analytics: false });
    trackCookieConsentUpdated('reject_all', false);
  };

  const handleSavePreferences = (): void => {
    setConsent(tempConsent);
    setShowDetails(false);
    trackCookieConsentUpdated('save_preferences', tempConsent.analytics);
  };

  const banner = (
    <div
      data-testid="cookie-consent-banner"
      className="pointer-events-none fixed bottom-3 left-3 right-3 z-[10000] sm:bottom-4 sm:left-auto sm:right-4 sm:max-w-sm"
    >
      <Card className="pointer-events-auto p-3 shadow-lg">
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <h3 className="m-0 text-sm font-semibold leading-tight text-card-foreground">
              {t('cookie_consent_title')}
            </h3>
            <p className="m-0 mt-1 text-xs leading-snug text-muted-foreground">
              {t('cookie_consent_description')}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <Button onClick={handleAcceptAll} variant="inverse" size="sm" className="h-7 px-2.5 text-xs">
                {t('cookie_consent_accept_all_short')}
              </Button>
              <Button
                onClick={() => setShowDetails(true)}
                variant="outline"
                size="sm"
                className="h-7 px-2"
                aria-label={t('cookie_consent_customize')}
              >
                <Settings className="h-3.5 w-3.5" />
              </Button>
              <Button onClick={handleRejectAll} variant="outline" size="sm" className="h-7 px-2.5 text-xs">
                {t('cookie_consent_reject_all_short')}
              </Button>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowBanner(false)}
            className="-mr-1 -mt-1 h-6 w-6 shrink-0"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </Card>
    </div>
  );

  return (
    <>
      {createPortal(banner, document.body)}

      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="max-w-[600px]">
          <DialogHeader>
            <DialogTitle>{t('cookie_consent_preferences_title')}</DialogTitle>
            <DialogDescription>{t('cookie_consent_preferences_description')}</DialogDescription>
          </DialogHeader>
          <div className="mt-4 flex flex-col gap-6">
            <div className="rounded-lg border border-border p-4">
              <div className="mb-2 flex items-center justify-between">
                <div>
                  <h4 className="m-0 text-base font-semibold text-foreground">
                    {t('cookie_consent_necessary_title')}
                  </h4>
                  <p className="mt-1 mb-0 text-sm text-muted-foreground">
                    {t('cookie_consent_necessary_description')}
                  </p>
                </div>
                <div className="flex items-center gap-2 text-[var(--color-success)]">
                  <Check className="h-5 w-5" />
                  <span className="text-sm font-medium">{t('cookie_consent_always_active')}</span>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-border p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h4 className="m-0 text-base font-semibold text-foreground">
                    {t('cookie_consent_analytics_title')}
                  </h4>
                  <p className="mt-1 mb-0 text-sm text-muted-foreground">
                    {t('cookie_consent_analytics_description')}
                  </p>
                </div>
                <Switch
                  className="ml-4"
                  checked={tempConsent.analytics}
                  onCheckedChange={(checked) => setTempConsent({ ...tempConsent, analytics: checked })}
                />
              </div>
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <CancelButton onClick={() => setShowDetails(false)} />
            <Button onClick={handleSavePreferences}>
              {t('cookie_consent_save_preferences')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
